import { Button } from '@/components/ui/button'
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react'

interface DomainProvisioningProgressProps {
  domain: string
  stage: string
  stageLabel: string
  isFailed: boolean
  error: string | null
  onRetry: () => void
  isRetrying: boolean
}

const STAGES = ['registering', 'configuring_dns', 'configuring_server', 'activating'] as const

const STAGE_INDEX: Record<string, number> = {
  initiated: 0,
  registering: 1,
  configuring_dns: 2,
  configuring_server: 3,
  activating: 4,
  active: 4,
}

function getSegmentIndex(stage: string): number {
  return STAGE_INDEX[stage] ?? 0
}

export function DomainProvisioningProgress({
  domain,
  stage,
  stageLabel,
  isFailed,
  error,
  onRetry,
  isRetrying,
}: DomainProvisioningProgressProps) {
  const segmentIndex = getSegmentIndex(stage)

  if (isFailed) {
    return (
      <div className="space-y-4">
        <div>
          <p className="font-medium">{domain}</p>
          <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {error || 'Domain setup failed'}
          </p>
        </div>
        <Button
          onClick={() => onRetry()}
          disabled={isRetrying}
          className="rounded"
        >
          {isRetrying ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </>
          )}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="font-medium">{domain}</p>
      <p className="text-sm text-muted-foreground">Setting up your domain...</p>

      <div
        className="flex gap-1"
        role="progressbar"
        aria-valuenow={segmentIndex}
        aria-valuemax={4}
        aria-label="Domain setup progress"
      >
        {STAGES.map((_, i) => {
          const segNum = i + 1
          const isCompleted = segmentIndex >= segNum
          const isCurrent = segmentIndex === i // current = the one being worked on

          return (
            <div
              key={i}
              className={`h-2 flex-1 rounded-full transition-all duration-500 ease-in-out ${
                isCompleted
                  ? 'bg-primary'
                  : isCurrent
                    ? 'bg-primary/60 animate-pulse'
                    : 'bg-muted'
              }`}
            />
          )
        })}
      </div>

      <p className="text-sm text-muted-foreground" aria-live="polite">
        {stageLabel}
      </p>
    </div>
  )
}
